// Render a contract template by substituting {{tokens}} with values from context.
//
// A "template" here is the same shape as an agreement template:
//   { name, body, clauses?, appliesTo }
//
// The body field contains HTML (from the contenteditable editor in AgreementsPage).
// Legacy templates may have a `clauses` array instead — we render that as a numbered
// "TERMS & CONDITIONS" section at the end if `body` doesn't already contain one.
//
// XSS hardening: every HTML string we emit is sanitised with DOMPurify so that
// template authors (or pasted content) can't inject <script> tags or event handlers.
// Signature images are base64 data URLs we generate ourselves, so we explicitly
// allow data: URIs on <img src>.

import DOMPurify from 'dompurify';

const SANITIZE_CONFIG = {
  ADD_TAGS: [],
  ADD_ATTR: ['target'],
  // Keep the signature image (data: URL we generated) and any normal http(s) image
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
};

export function sanitizeHtml(html) {
  if (!html) return '';
  return DOMPurify.sanitize(String(html), SANITIZE_CONFIG);
}

/**
 * Substitute every {{token}} in a string with values from the context map.
 * Unknown tokens are left as-is so the template author can spot typos.
 */
export function substituteTokens(html, context) {
  if (!html) return '';
  return String(html).replace(/\{\{\s*([a-z0-9_]+)\s*\}\}/gi, (full, _name) => {
    const key = full.replace(/\s/g, '');
    return context[key] != null ? context[key] : full;
  });
}

/**
 * Render a contract template to HTML.
 *
 * @param {object} template  { name, body, clauses?, appliesTo }
 * @param {object} context   token → value map from buildContractContext()
 * @returns {string} HTML string ready for innerHTML / print, sanitised against XSS
 */
export function renderContract(template, context) {
  if (!template) return '';
  const body = template.body || '';
  let html = substituteTokens(body, context);

  // Legacy support: if no body but clauses exist, render clauses as a T&C section
  if (!body && template.clauses && template.clauses.length > 0) {
    html = '<h2 style="font-size:14px;text-transform:uppercase;letter-spacing:.05em;margin:24px 0 12px 0;">Terms &amp; Conditions</h2><ol style="padding-left:20px;line-height:1.7;">'
      + template.clauses.map(c => `<li style="margin-bottom:8px;">${substituteTokens(c, context)}</li>`).join('')
      + '</ol>';
  } else if (body && template.clauses && template.clauses.length > 0 && !/terms\s*(?:&amp;|&|and)\s*conditions/i.test(body)) {
    // Body exists but no T&C section in it — append clauses as legacy fallback
    html += '<h2 style="font-size:14px;text-transform:uppercase;letter-spacing:.05em;margin:24px 0 12px 0;">Terms &amp; Conditions</h2><ol style="padding-left:20px;line-height:1.7;">'
      + template.clauses.map(c => `<li style="margin-bottom:8px;">${substituteTokens(c, context)}</li>`).join('')
      + '</ol>';
  }

  return sanitizeHtml(html);
}

/**
 * Open a print window with a fully rendered contract.
 *
 * @param {object} template
 * @param {object} context
 * @param {object} [opts]
 * @param {string} [opts.title]  Window title (defaults to template name + contract id)
 * @param {boolean} [opts.autoPrint=true]  Auto-trigger window.print() once loaded
 */
export function printContract(template, context, opts = {}) {
  const w = window.open('', '_blank');
  if (!w) return null;

  const title = opts.title
    || `${template?.name || 'Contract'}${context?.['{{contract_id}}'] ? ' · ' + context['{{contract_id}}'] : ''}`;
  const autoPrint = opts.autoPrint !== false;

  // renderContract already sanitises; print uses the same output
  const rendered = renderContract(template, context);

  w.document.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8" /><title>${title.replace(/</g, '&lt;')}</title>
<style>
  body { font-family: 'Segoe UI', Arial, sans-serif; color: #222; padding: 40px; max-width: 800px; margin: 0 auto; font-size: 13px; line-height: 1.65; }
  h1 { font-size: 22px; margin: 0 0 4px 0; }
  h2 { font-size: 16px; margin: 22px 0 10px 0; color: #111; }
  h3 { font-size: 14px; margin: 16px 0 8px 0; }
  p  { margin: 0 0 10px 0; }
  ol, ul { padding-left: 20px; margin: 0 0 12px 0; }
  ol li, ul li { margin-bottom: 6px; }
  strong, b { font-weight: 700; }
  em, i { font-style: italic; }
  table { border-collapse: collapse; margin: 0 0 12px 0; }
  table td, table th { padding: 4px 8px; vertical-align: top; }
  img { max-width: 100%; }
  hr { border: none; border-top: 1px solid #ddd; margin: 18px 0; }
  .contract-meta { color: #666; font-size: 11px; margin-bottom: 18px; }
  @media print { body { padding: 16px; max-width: none; } }
</style>
</head><body>
${rendered}
${autoPrint ? '<script>window.onload=function(){setTimeout(function(){window.print();}, 200);}</script>' : ''}
</body></html>`);
  w.document.close();
  w.focus();
  return w;
}
